import React from 'react';
import { Candidate } from '../types/hr.types';
import { Clock, Phone, Calendar, FileText, ChevronRight, MoreHorizontal, MapPin, AlertCircle, Users, CheckCircle2, ChevronUp } from 'lucide-react';
import { motion } from 'framer-motion';
interface CandidateTableProps {
  candidates: Candidate[];
  onCandidateClick: (c: Candidate) => void;
  onScheduleInterview?: (c: Candidate) => void;
  onHire?: (c: Candidate) => void;
  onSendOffer?: (c: Candidate) => void;
  onSetFollowUp: (c: Candidate) => void;
  onStatusChange?: (c: Candidate, newStatus: Candidate['status']) => void;
  onAdvanceOnboarding?: (c: Candidate, next: Candidate['status']) => void;
  nextDateLabel?: string;
  emptyMessage?: string;
}
// Onboarding pipeline: Offer Accepted -> Pre-boarding -> Ready to Start -> (done)
const ONBOARDING_NEXT: Partial<Record<Candidate['status'], {
  next: Candidate['status'];
  label: string;
}>> = {
  'Offer Accepted': {
    next: 'Pre-boarding',
    label: 'Create Company Directory'
  },
  'Pre-boarding': {
    next: 'Ready to Start',
    label: 'Create Company Directory'
  }
};
const FOLLOW_UP_STATUSES = new Set(['Follow Up Later', 'Hired Elsewhere', 'Offer Declined']);
const OFFER_EDITABLE_STATUSES = new Set<Candidate['status']>(['Offer Sent', 'Waiting on Response', 'Offer Accepted', 'Offer Declined', 'Follow Up Later']);
const OFFER_STATUS_OPTIONS: Candidate['status'][] = ['Offer Sent', 'Waiting on Response', 'Offer Accepted', 'Offer Declined', 'Follow Up Later'];
const STATUS_STYLES: Record<string, {
  bg: string;
  text: string;
  dot: string;
}> = {
  'New Lead': {
    bg: 'bg-slate-50',
    text: 'text-slate-700',
    dot: 'bg-slate-400'
  },
  'Phone Screen': {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    dot: 'bg-purple-500'
  },
  'In Person Interview': {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    dot: 'bg-orange-500'
  },
  'Offer Sent': {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500'
  },
  'Waiting on Response': {
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    dot: 'bg-yellow-500'
  },
  'Offer Accepted': {
    bg: 'bg-green-50',
    text: 'text-green-700',
    dot: 'bg-green-500'
  },
  'Pre-boarding': {
    bg: 'bg-green-50',
    text: 'text-green-700',
    dot: 'bg-green-500'
  },
  'Ready to Start': {
    bg: 'bg-green-50',
    text: 'text-green-700',
    dot: 'bg-green-500'
  },
  'Offer Declined': {
    bg: 'bg-red-50',
    text: 'text-red-700',
    dot: 'bg-red-500'
  },
  'Hired Elsewhere': {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    dot: 'bg-gray-500'
  },
  'Follow Up Later': {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-500'
  },
  Declined: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    dot: 'bg-red-500'
  }
};
function EscalationTier({
  days
}: {
  days: number;
}) {
  if (days < 1) return null;
  let tier = 'Recruiter';
  let cls = 'bg-amber-100 text-amber-700';
  if (days >= 5) {
    tier = 'Director of Ops';
    cls = 'bg-red-100 text-red-700';
  } else if (days >= 2) {
    tier = 'HR Coordinator';
    cls = 'bg-orange-100 text-orange-700';
  }
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold mt-0.5 ${cls}`} title={`Overdue ${days} day${days === 1 ? '' : 's'} — escalated to ${tier}`} data-id="element-438">
      <ChevronUp className="h-2.5 w-2.5" data-id="element-439" />
      Escalated to {tier}
    </span>;
}
function StatusBadge({
  status
}: {
  status: Candidate['status'];
}) {
  const s = STATUS_STYLES[status] || STATUS_STYLES['New Lead'];
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.bg} ${s.text}`} data-id="element-440">
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} data-id="element-441" />
      {status}
    </span>;
}
function OfferStatusSelect({
  candidate,
  onChange
}: {
  candidate: Candidate;
  onChange: (next: Candidate['status']) => void;
}) {
  const s = STATUS_STYLES[candidate.status] || STATUS_STYLES['New Lead'];
  return <div className={`relative inline-flex items-center gap-1.5 pl-2.5 pr-7 py-1 rounded-full text-xs font-medium ${s.bg} ${s.text} ring-1 ring-inset ring-black/5`} onClick={e => e.stopPropagation()} data-id="element-442">
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} data-id="element-443" />
      <span data-id="element-444">{candidate.status}</span>
      <ChevronRight className="absolute right-1.5 h-3 w-3 rotate-90 opacity-60 pointer-events-none" data-id="element-445" />
      <select value={candidate.status} onChange={e => onChange(e.target.value as Candidate['status'])} onClick={e => e.stopPropagation()} aria-label="Change offer status" className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" data-id="element-446">
        {OFFER_STATUS_OPTIONS.map(opt => <option key={opt} value={opt} data-id="element-447">
            {opt}
          </option>)}
      </select>
    </div>;
}
function formatRelativeDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
function formatTime(iso?: string): string | null {
  if (!iso) return null;
  // Date-only strings (YYYY-MM-DD) have no time component — skip.
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  // Skip midnight (00:00) which usually indicates an unset time.
  if (d.getHours() === 0 && d.getMinutes() === 0) return null;
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  });
}
function daysFromToday(iso?: string): number | null {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}
export function CandidateTable({
  candidates,
  onCandidateClick,
  onScheduleInterview,
  onHire,
  onSendOffer,
  onSetFollowUp,
  onStatusChange,
  onAdvanceOnboarding,
  nextDateLabel = 'Next Date',
  emptyMessage = 'No candidates in this stage yet.'
}: CandidateTableProps) {
  if (candidates.length === 0) {
    return <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-16 px-6 text-center" data-id="element-448">
        <div className="mx-auto h-12 w-12 rounded-full bg-gray-50 flex items-center justify-center mb-3" data-id="element-449">
          <FileText className="h-6 w-6 text-gray-400" data-id="element-450" />
        </div>
        <p className="text-sm font-semibold text-gray-900" data-id="element-451">{emptyMessage}</p>
        <p className="text-xs text-gray-500 mt-1" data-id="element-452">
          Add a candidate or move existing ones to this stage.
        </p>
      </div>;
  }
  return <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-453">
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto" data-id="element-454">
        <table className="w-full text-sm" data-id="element-455">
          <thead className="bg-gray-50/80 border-b border-gray-100" data-id="element-456">
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-457">
              <th className="px-5 py-3" data-id="element-458">Candidate</th>
              <th className="px-5 py-3" data-id="element-459">Role</th>
              <th className="px-5 py-3" data-id="element-460">Stage</th>
              <th className="px-5 py-3" data-id="element-461">Recruiter</th>
              <th className="px-5 py-3" data-id="element-462">{nextDateLabel}</th>
              <th className="px-5 py-3" data-id="element-463">Follow-up</th>
              <th className="px-5 py-3 text-right" data-id="element-464">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-465">
            {candidates.map((c, idx) => {
            const followUpDays = daysFromToday(c.followUpDate);
            const overdue = followUpDays !== null && followUpDays < 0;
            const dueToday = followUpDays === 0;
            const nextDate = c.status === 'In Person Interview' ? c.inPersonInterviewDate : c.status === 'Offer Sent' || c.status === 'Waiting on Response' ? c.offerSentDate : c.status === 'Offer Accepted' || c.status === 'Pre-boarding' || c.status === 'Ready to Start' ? c.desiredStartDate : c.status === 'Follow Up Later' || c.status === 'Hired Elsewhere' || c.status === 'Offer Declined' ? c.followUpDate || c.offerDecisionDate : c.phoneInterviewDate;
            return <motion.tr key={c.id} initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.02
            }} className="hover:bg-orange-50/30 transition-colors group cursor-pointer" onClick={() => onCandidateClick(c)} data-id="element-466">
                  <td className="px-5 py-3.5" data-id="element-467">
                    <div className="flex items-center gap-3" data-id="element-468">
                      <div className="h-9 w-9 rounded-full bg-gradient-to-br from-gray-100 to-gray-50 border border-gray-200 flex items-center justify-center text-xs font-semibold text-gray-700 flex-shrink-0" data-id="element-469">
                        {c.firstName[0]}
                        {c.lastName[0]}
                      </div>
                      <div className="min-w-0" data-id="element-470">
                        <p className="font-semibold text-gray-900 truncate group-hover:text-orange-700 transition-colors" data-id="element-471">
                          {c.firstName} {c.lastName}
                        </p>
                        <p className="text-xs text-gray-500 truncate" data-id="element-472">
                          {c.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5" data-id="element-473">
                    <p className="text-gray-900 font-medium" data-id="element-474">{c.role}</p>
                    <p className="text-xs text-gray-500" data-id="element-475">{c.department}</p>
                  </td>
                  <td className="px-5 py-3.5" data-id="element-476">
                    {onStatusChange && OFFER_EDITABLE_STATUSES.has(c.status) ? <OfferStatusSelect candidate={c} onChange={next => onStatusChange(c, next)} data-id="element-477" /> : <StatusBadge status={c.status} data-id="element-478" />}
                  </td>
                  <td className="px-5 py-3.5" data-id="element-479">
                    <span className="text-xs text-gray-700" data-id="element-480">
                      {c.recruiter || c.interviewedBy}
                    </span>
                  </td>
                  <td className="px-5 py-3.5" data-id="element-481">
                    <div className="flex flex-col leading-tight" data-id="element-482">
                      <span className="text-xs text-gray-700" data-id="element-483">
                        {formatRelativeDate(nextDate)}
                      </span>
                      {formatTime(nextDate) && <span className="text-[11px] text-gray-500 mt-0.5" data-id="element-484">
                          {formatTime(nextDate)}
                        </span>}
                    </div>
                  </td>
                  <td className="px-5 py-3.5" data-id="element-485">
                    {c.followUpDate ? <div className={`flex flex-col leading-tight ${overdue ? 'text-red-700' : dueToday ? 'text-orange-700' : 'text-gray-700'}`} data-id="element-486">
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-487">
                          {overdue ? <AlertCircle className="h-3.5 w-3.5" data-id="element-488" /> : <Clock className="h-3.5 w-3.5" data-id="element-489" />}
                          {overdue ? `${Math.abs(followUpDays!)}d overdue` : dueToday ? 'Today' : formatRelativeDate(c.followUpDate)}
                        </span>
                        {overdue && <EscalationTier days={Math.abs(followUpDays!)} data-id="element-490" />}
                        {formatTime(c.followUpDate) && <span className="text-[11px] text-gray-500 mt-0.5 pl-5" data-id="element-491">
                            {formatTime(c.followUpDate)}
                          </span>}
                      </div> : <span className="text-xs text-gray-400" data-id="element-492">—</span>}
                  </td>
                  <td className="px-5 py-3.5" data-id="element-493">
                    <div className="flex items-center justify-end gap-1" data-id="element-494">
                      {onAdvanceOnboarding && ONBOARDING_NEXT[c.status] && <button onClick={e => {
                    e.stopPropagation();
                    const step = ONBOARDING_NEXT[c.status]!;
                    onAdvanceOnboarding(c, step.next);
                  }} title={ONBOARDING_NEXT[c.status]!.label} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-700 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors" data-id="element-495">
                          <ChevronRight className="h-3.5 w-3.5" data-id="element-496" />
                          <span className="hidden xl:inline" data-id="element-497">
                            {ONBOARDING_NEXT[c.status]!.label}
                          </span>
                        </button>}
                      {c.status === 'Phone Screen' && onSendOffer && <button onClick={e => {
                    e.stopPropagation();
                    onSendOffer(c);
                  }} title="Send Offer" className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors" data-id="element-498">
                          <FileText className="h-3.5 w-3.5" data-id="element-499" />
                          <span className="hidden xl:inline" data-id="element-500">Send Offer</span>
                        </button>}
                      {(FOLLOW_UP_STATUSES.has(c.status) || c.status === 'Phone Screen') && onScheduleInterview && <button onClick={e => {
                    e.stopPropagation();
                    onScheduleInterview(c);
                  }} title="Schedule interview" className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors" data-id="element-501">
                            <Users className="h-3.5 w-3.5" data-id="element-502" />
                            <span className="hidden xl:inline" data-id="element-503">Interview</span>
                          </button>}
                      {FOLLOW_UP_STATUSES.has(c.status) && onHire && <button onClick={e => {
                    e.stopPropagation();
                    onHire(c);
                  }} title="Mark as hired" className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-700 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors" data-id="element-504">
                          <CheckCircle2 className="h-3.5 w-3.5" data-id="element-505" />
                          <span className="hidden xl:inline" data-id="element-506">Hire</span>
                        </button>}
                      <button onClick={e => {
                    e.stopPropagation();
                    onSetFollowUp(c);
                  }} title="Set follow-up" className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors" data-id="element-507">
                        <Clock className="h-3.5 w-3.5" data-id="element-508" />
                        <span className="hidden lg:inline" data-id="element-509">Follow-up</span>
                      </button>
                      <button onClick={e => {
                    e.stopPropagation();
                    onCandidateClick(c);
                  }} title="View details" className="inline-flex items-center justify-center h-7 w-7 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-md transition-colors" data-id="element-510">
                        <ChevronRight className="h-4 w-4" data-id="element-511" />
                      </button>
                    </div>
                  </td>
                </motion.tr>;
          })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-gray-100" data-id="element-512">
        {candidates.map(c => {
        const followUpDays = daysFromToday(c.followUpDate);
        const overdue = followUpDays !== null && followUpDays < 0;
        return <div key={c.id} onClick={() => onCandidateClick(c)} className="p-4 hover:bg-gray-50 cursor-pointer" data-id="element-513">
              <div className="flex items-start justify-between gap-3" data-id="element-514">
                <div className="flex items-center gap-3 min-w-0" data-id="element-515">
                  <div className="h-10 w-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-xs font-semibold text-gray-700" data-id="element-516">
                    {c.firstName[0]}
                    {c.lastName[0]}
                  </div>
                  <div className="min-w-0" data-id="element-517">
                    <p className="font-semibold text-gray-900 truncate" data-id="element-518">
                      {c.firstName} {c.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate" data-id="element-519">{c.role}</p>
                  </div>
                </div>
                <button onClick={e => {
              e.stopPropagation();
              onSetFollowUp(c);
            }} className="flex-shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-orange-700 hover:bg-orange-50" data-id="element-520">
                  <Clock className="h-4 w-4" data-id="element-521" />
                </button>
              </div>
              <div className="mt-3 flex items-center justify-between" data-id="element-522">
                <StatusBadge status={c.status} data-id="element-523" />
                {c.followUpDate && <span className={`text-xs font-medium ${overdue ? 'text-red-600' : 'text-gray-600'}`} data-id="element-524">
                    {overdue ? `${Math.abs(followUpDays!)}d overdue` : `Follow-up ${formatRelativeDate(c.followUpDate)}`}
                  </span>}
              </div>
            </div>;
      })}
      </div>
    </div>;
}